import { inputToTags, tagsToInput } from "../../mst/kinds/utils";
import { getDefaultBaseInput, OptimisticConcurrency } from "../../schema/types/base";
import { GroupCreate } from "../../schema/types/group/create";
import { Group } from "../../schema/types/group/entity";
import { GroupInput } from "../../schema/types/group/input";
import { getDefaultQueryInput, inputToQuery, queryToInput } from "../../schema/types/query/input";
import { parseLink } from "../../utils/linkParser/linkParser";

export function getDefaultGroupInput(): GroupInput {
  return {
    ...getDefaultBaseInput(),
    id: "",
    version: 0,
    serviceAccounts: [],
    users: [],
    userQuery: getDefaultQueryInput(),
    identityMatcher: {
      isEnabled: false,
      language: "jmespath",
      expression: "",
      isTestUICollapsed: true,
    },
    meta: {
      isEdit: false,
      queryResult: {
        user: {
          isCollapsed: true,
          data: null,
        },
      },
    },
  };
}

export function inputToGroup(input: GroupInput): GroupCreate {
  const res: GroupCreate = {
    name: input.name,
    kind: "group",
    tags: inputToTags(input.tags),
    origin: "default",
    memberLinks: [],
  };
  if (input.userQuery.isEnabled) {
    res.memberQuery = inputToQuery(input.userQuery, "user");
  }
  if (input.description) {
    res.description = input.description;
  }
  if (input.users.length > 0) {
    res.memberLinks = res.memberLinks!.concat(input.users.map((u) => u.link));
  }
  if (input.serviceAccounts.length > 0) {
    res.memberLinks = res.memberLinks!.concat(input.serviceAccounts.map((u) => u.link));
  }
  if (input.identityMatcher.isEnabled) {
    res.identityMatcher = {
      language: input.identityMatcher.language,
      expression: input.identityMatcher.expression,
    };
  }
  return res;
}

export function groupToInput(group: Group): GroupInput {
  const def = getDefaultGroupInput();
  const res: GroupInput = {
    ...def,
    id: group.id,
    version: group.version,
    name: group.name,
    description: group.description,
    tags: tagsToInput(group.tags),
    userQuery: queryToInput(group.memberQuery),
    users: (group.memberLinks || [])
      .filter((l) => l.includes(`/user/`))
      .map((l) => {
        const { name } = parseLink(l, { useInputCtx: true });
        return {
          value: name,
          name: name,
          link: l,
        };
      }),
    serviceAccounts: (group.memberLinks || [])
      .filter((l) => l.includes(`/serviceaccount/`))
      .map((l) => {
        const { name } = parseLink(l, { useInputCtx: true });
        return {
          value: name,
          name: name,
          link: l,
        };
      }),
    meta: {
      ...def.meta,
      isEdit: true,
    },
  };

  if (group.identityMatcher?.expression) {
    res.identityMatcher = {
      isEnabled: true,
      language: group.identityMatcher.language,
      expression: group.identityMatcher.expression,
      isTestUICollapsed: true,
    };
  }

  return res;
}

export function groupToPatch(_group: GroupCreate, current: Group, concurrency: OptimisticConcurrency) {
  const group: any = JSON.parse(JSON.stringify(_group));

  group[`$replace/memberLinks`] = group.memberLinks;
  delete group.memberLinks;

  if (current.memberQuery && !_group.memberQuery) {
    group.memberQuery = null as any;
  }

  if (current.identityMatcher && !_group.identityMatcher) {
    group.identityMatcher = null as any;
  }

  group[`$replace/tags`] = group.tags;
  delete group.tags;

  group.id = concurrency.id;
  group.version = concurrency.version;

  return group;
}
